import { logger } from 'modules/logger';

export const POWERED_BY_SIRONA_CLASSIFICATIONS = 'Powered by Sirona Intelligent Classifications';

export const SNACKBAR_CONTENT_PROPS = { reduceCloseButtonHeight: true } as const;

export const HP_MODAL_VARIANTS = Object.freeze({
  SAVE: 'save',
  UPDATE: 'update',
  DELETE: 'delete',
  SEARCH: 'search',
});

export type WidgetMode = (typeof HP_MODAL_VARIANTS)[keyof typeof HP_MODAL_VARIANTS] | null;

export const SUBMIT_FORM_INTENT = Object.freeze({
  SAVE: HP_MODAL_VARIANTS.SAVE,
  UPDATE: HP_MODAL_VARIANTS.UPDATE,
});

export type SubmitFormIntent = (typeof SUBMIT_FORM_INTENT)[keyof typeof SUBMIT_FORM_INTENT];

/**
 * Whether a viewport's series classification is settled, and if not, why not.
 *
 * RESOLVED includes an empty criteria list. A description the classifier maps to nothing, and a
 * viewport the reader deliberately cleared, are both real answers and both save fine. What must
 * never be saved is PENDING or FAILED: storing those sends the backend an empty criteria list, which
 * it reads as "this viewport has no requirements", after which the viewport matches nothing and is
 * handed whatever series is left over at hang time (VX-4977).
 */
export const CLASSIFICATION_STATE = Object.freeze({
  PENDING: 'pending',
  FAILED: 'failed',
  RESOLVED: 'resolved',
});

export type ClassificationState = (typeof CLASSIFICATION_STATE)[keyof typeof CLASSIFICATION_STATE];

export const HANGING_PROTOCOL_PHASE = Object.freeze({
  TWO: 'phase-2',
  THREE: 'phase-3',
  FOUR: 'phase-4',
  FOUR_STEPS: 'phase-4-steps',
  FIVE: 'phase-5',
});

export type HangingProtocolPhase =
  (typeof HANGING_PROTOCOL_PHASE)[keyof typeof HANGING_PROTOCOL_PHASE];

// Ordered oldest to newest. `isAtLeastPhase4` ranks by position here instead of enumerating the
// treatments it accepts: a whitelist silently regressed callers to the oldest UI branch whenever a
// newer treatment was rolled out, which is what produced VX-4752 (`phase-4-steps` matched nothing
// and fell through to the legacy phase-2 dialog). `phase-5` is already defined and would have hit
// the same trap.
const PHASE_ORDER: readonly string[] = [
  HANGING_PROTOCOL_PHASE.TWO,
  HANGING_PROTOCOL_PHASE.THREE,
  HANGING_PROTOCOL_PHASE.FOUR,
  HANGING_PROTOCOL_PHASE.FOUR_STEPS,
  HANGING_PROTOCOL_PHASE.FIVE,
];

// These helpers run on every render, so warn only once per distinct unknown treatment.
const warnedUnknownPhases = new Set<string | undefined>();

/**
 * Position of `phase` in `PHASE_ORDER`, or -1 when the treatment is not recognized.
 *
 * An unrecognized treatment fails safe: callers fall back to the default phase behavior rather
 * than assuming a newer one. That fallback is the legacy branch, so it is logged instead of
 * happening silently -- an unhandled rollout value should be visible in Datadog, not something a
 * user has to notice and report.
 */
function phaseRank(phase: string | undefined): number {
  const rank = PHASE_ORDER.indexOf(phase as string);

  if (rank === -1 && !warnedUnknownPhases.has(phase)) {
    warnedUnknownPhases.add(phase);
    logger.warn(
      `Unrecognized HANGING_PROTOCOL_PHASE treatment "${phase}"; falling back to default phase behavior. Add it to PHASE_ORDER if this is a new rollout value.`
    );
  }

  return rank;
}

// Stepped HP has a hard dependency on phase-4 behavior (V4 form, viewport classifications, etc.),
// so `phase-4-steps` is treated as a superset of `phase-4` everywhere phase-4 gates UI/behavior.
export function isAtLeastPhase4(phase: string | undefined): boolean {
  return phaseRank(phase) >= PHASE_ORDER.indexOf(HANGING_PROTOCOL_PHASE.FOUR);
}

export function isPhase4Steps(phase: string | undefined): boolean {
  return phase === HANGING_PROTOCOL_PHASE.FOUR_STEPS;
}
